import { FC, useCallback, useMemo } from "react";
import {
  CRMChannel,
  ChatMessageAttachmentType,
  ChatMessagePayloadAttachment,
  WhatsappMessageModel,
  WhatsappMessageType,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { ChatMessageBubbleAction } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageBubbleAction";
import { ChatMessageReplyInfo } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageReplyInfo";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { ChatMessageBubbleContextValue } from "@web-src/modules/chats/components/CommonMessageBubble/providers/ChatMessageBubbleProvider/ChatMessageBubbleContext";
import { CommonMessageBubble } from "@web-src/modules/chats/components/CommonMessageBubble";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { environment } from "@web-src/environments/environment";
import { tokenizeMessageBody } from "@web-src/modules/chats/utils";
import { CHAT_MESSAGE_PAYLOAD_VERSION } from "@jugl-web/utils/consts";
import mime from "mime";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { isWhatsappPayloadSupported } from "../../../../utils/isWhatsappPayloadSupported";
import { useGetSenderDetails } from "../../hooks/useGetSenderDetails";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";

export type WhatsappMessageBubbleProps = {
  message: WhatsappMessageModel;
  readOnly?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isPreview?: boolean;
  onSelect?: (messageId: string, isSelected: boolean) => void;
  onTriggerAction?: (
    action: ChatMessageBubbleAction,
    message: WhatsappMessageModel
  ) => void;
  onReplyPreviewClick?: (replyInfo: ChatMessageReplyInfo) => void;
  onReaction?: (reaction: string) => void;
  isMessageActionsDisabled?: boolean;
  isFirstUnread?: boolean;
  groupInfo?: {
    isFirst?: boolean;
    isLast?: boolean;
  };
};

export const WhatsappMessageBubble: FC<WhatsappMessageBubbleProps> = ({
  message,
  readOnly,
  isSelectable,
  isSelected,
  onSelect,
  onTriggerAction,
  onReaction,
  isMessageActionsDisabled,
  groupInfo,
  onReplyPreviewClick,
  isHighlighted,
  isPreview,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { getSenderDetails } = useGetSenderDetails();
  const { setOpenedUserProfile } = useClientSupportPageContext();
  const { isCx } = useAppVariant();
  const { t } = useTranslations();

  const getMediaId = useCallback((msg: WhatsappMessageModel) => {
    switch (msg.payload.type) {
      case WhatsappMessageType.image:
        return msg.payload.image?.id;
      case WhatsappMessageType.video:
        return msg.payload.video?.id;
      case WhatsappMessageType.sticker:
        return msg.payload.sticker?.id;
      case WhatsappMessageType.audio:
        return msg.payload.audio?.id;
      case WhatsappMessageType.document:
        return msg.payload.document?.id;
      default:
        return undefined;
    }
  }, []);

  const mediaId = useMemo(() => getMediaId(message), [getMediaId, message]);

  const { data: attachmentFile } =
    integrationsApi.useGetWhatsappMessageMediaQuery(
      mediaId && message.payload.type === WhatsappMessageType.image
        ? { entityId, mediaId }
        : skipToken
    );

  const isUnsupported = useMemo(
    () => !isWhatsappPayloadSupported(message.payload.type),
    [message.payload.type]
  );

  const getAttachmentConfig = useCallback(
    (msg: WhatsappMessageModel, file?: Blob) => {
      if (
        ![
          WhatsappMessageType.image,
          WhatsappMessageType.video,
          WhatsappMessageType.sticker,
          WhatsappMessageType.audio,
          WhatsappMessageType.document,
        ].includes(msg.payload.type)
      ) {
        return undefined;
      }

      let attachmentUrl = `${
        environment.apiUrl
      }/api/${entityId}/agents/whatsapp/media?id=${getMediaId(msg)}`;
      if (msg.payload.type === "image" && msg.payload.image?.link) {
        attachmentUrl = msg.payload.image.link;
      }
      let mimetype: string | undefined = file?.type;
      let attachmentType: ChatMessageAttachmentType | undefined;
      let name: string | undefined;
      switch (msg.payload.type) {
        case WhatsappMessageType.sticker:
          mimetype = msg.payload.sticker?.mime_type || mimetype || "image/*";
          attachmentType = ChatMessageAttachmentType.gifDirectUrl;
          break;
        case WhatsappMessageType.image:
          mimetype = msg.payload.image?.mime_type || mimetype || "image/*";
          attachmentType = ChatMessageAttachmentType.image;
          break;
        case WhatsappMessageType.video:
          mimetype = msg.payload.video?.mime_type || mimetype || "video/*";
          attachmentType = ChatMessageAttachmentType.video;
          break;
        case WhatsappMessageType.audio:
          mimetype = msg.payload.audio?.mime_type || mimetype || "audio/*";
          attachmentType = ChatMessageAttachmentType.audio;
          name = msg.payload.audio?.filename;
          break;
        case WhatsappMessageType.document:
          mimetype =
            msg.payload.document?.mime_type ||
            mime.getType(msg.payload.document?.filename || "") ||
            mimetype;
          attachmentType = ChatMessageAttachmentType.otherFile;
          name = msg.payload.document?.filename;
          break;
        default:
          attachmentType = ChatMessageAttachmentType.otherFile;
      }
      if (!mimetype) {
        mimetype = "application/octet-stream";
      }
      return {
        type: attachmentType,
        url: attachmentUrl,
        mimetype,
        name,
        size: file?.size || 0,
        uid: Math.round(Math.random() * 1e8).toString(),
      };
    },
    [entityId, getMediaId]
  );

  const attachment = useMemo(
    () => getAttachmentConfig(message, attachmentFile),
    [attachmentFile, getAttachmentConfig, message]
  );

  // Get message text body
  const messageTextBody = useMemo(() => {
    switch (message.payload.type) {
      case WhatsappMessageType.text:
        if (message.type === "info") {
          return message.payload.body;
        }
        return message.payload.text?.body || "";
      case WhatsappMessageType.image:
        return message.payload.image?.caption || "";
      case WhatsappMessageType.video:
        return message.payload.video?.caption || "";
      case WhatsappMessageType.document:
        return message.payload.document?.caption || "";
      default:
        return "";
    }
  }, [message]);

  const previewUrl = useMemo(() => {
    if (!messageTextBody) return undefined;
    return tokenizeMessageBody(messageTextBody).find(
      (item) => item.type === "url"
    )?.rawString;
  }, [messageTextBody]);

  const handleTriggerAction = useCallback(
    (action: ChatMessageBubbleAction) => {
      onTriggerAction?.(action, message);
    },
    [message, onTriggerAction]
  );

  const handleTriggerReaction = useCallback(
    (reaction: string) => {
      onReaction?.(reaction);
    },
    [onReaction]
  );

  const handleAvatarClick = useCallback(
    (userId: string) => {
      setOpenedUserProfile(userId);
    },
    [setOpenedUserProfile]
  );

  const isOutgoing = message.type === "outgoing";

  // Extract reply info from the message model's reply property
  const replyInfo = useMemo(() => {
    if (message.reply) {
      let replyMsgBody = "";
      if (message.reply.payload.type === WhatsappMessageType.text) {
        if (message.reply.type === "info") {
          replyMsgBody = message.reply.payload.body;
        } else {
          replyMsgBody = message.reply.payload.text?.body || "";
        }
      } else if (message.reply.payload.type === WhatsappMessageType.image) {
        replyMsgBody =
          message.reply.payload.image?.caption ||
          t({ id: "common.image", defaultMessage: "Image" });
      } else if (message.reply.payload.type === WhatsappMessageType.video) {
        replyMsgBody =
          message.reply.payload.video?.caption ||
          t({ id: "common.video", defaultMessage: "Video" });
      } else if (message.reply.payload.type === WhatsappMessageType.audio) {
        replyMsgBody =
          message.reply.payload.audio?.filename ||
          t({ id: "common.audio", defaultMessage: "Audio" });
      } else if (message.reply.payload.type === WhatsappMessageType.document) {
        replyMsgBody =
          message.reply.payload.document?.filename ||
          t({ id: "common.document", defaultMessage: "Document" });
      } else if (message.reply.payload.type === WhatsappMessageType.sticker) {
        replyMsgBody = t({ id: "common.sticker", defaultMessage: "Sticker" });
      } else {
        replyMsgBody = t({
          id: "client-support-page.unsupported-message-format",
          defaultMessage: "Unsupported message format",
        });
      }

      const replyAttachment = getAttachmentConfig(message.reply);

      // Create reply info object
      return {
        replyMsgId: message.reply.msg_id,
        replyMsgBody,
        replyMsgUser: message.reply.from,
        replyUserName: getSenderDetails(message.reply).senderName,
        replyAttachments: replyAttachment ? [replyAttachment] : undefined,
      };
    }

    return undefined;
  }, [message.reply, getAttachmentConfig, getSenderDetails, t]);

  const chatMessageBubbleProps: ChatMessageBubbleContextValue = useMemo(() => {
    const availableActions = [ChatMessageBubbleAction.REPLY];

    if (!isUnsupported) {
      if (messageTextBody) {
        availableActions.push(ChatMessageBubbleAction.COPY);
      }

      if (message.type === "incoming") {
        availableActions.push(ChatMessageBubbleAction.REACTION);
      }
    }

    return {
      messageId: message.msg_id,
      isDeleted: false,
      isUnsupported,
      isForwarded: false,
      messageClientId: message.id,
      messageFrom: message.from,
      isOutgoing,
      isGroupChat: true,
      showOutgoingSenderDetails: true,
      readOnly: !!readOnly,
      entityId,
      messageTextBody,
      previewUrl,
      // NOTE: always use the latest payload version for whatsapp as it's static
      messagePayloadVersion: CHAT_MESSAGE_PAYLOAD_VERSION,
      isMediaMessageOnly:
        !!attachment &&
        !messageTextBody &&
        [
          ChatMessageAttachmentType.image,
          ChatMessageAttachmentType.video,
        ].includes(attachment.type),
      isSelectable,
      isSelected,
      onSelect,
      triggerAction: handleTriggerAction,
      triggerReaction: handleTriggerReaction,
      isMessageActionsDisabled,
      groupInfo,
      onAvatarClick: !isCx ? handleAvatarClick : undefined,
      onReplyPreviewClick,
      isHighlighted,
      isPreview,
      isEdited: false,
      outgoingStatus: isOutgoing ? (message.read ? "read" : "sent") : undefined,
      timestamp: message.payload.timestamp,
      getAttachmentUrls: (a: ChatMessagePayloadAttachment) => ({
        preview: a.url || "",
        stream: a.url || "",
      }),
      replyInfo,
      availableActions,
      reactions: message.payload.reaction
        ? {
            summary: [
              { reaction: message.payload.reaction, users: [], count: 1 },
            ],
            myReactions:
              message.type === "outgoing" ? [message.payload.reaction] : [],
          }
        : undefined,
      isReactionDetailsDisabled: true,
      attachments: attachment ? [attachment] : undefined,
      ...getSenderDetails(message, CRMChannel.whatsapp),
    };
  }, [
    isUnsupported,
    message,
    isOutgoing,
    readOnly,
    entityId,
    messageTextBody,
    previewUrl,
    attachment,
    isSelectable,
    isSelected,
    onSelect,
    handleTriggerAction,
    handleTriggerReaction,
    isMessageActionsDisabled,
    groupInfo,
    isCx,
    handleAvatarClick,
    onReplyPreviewClick,
    isHighlighted,
    isPreview,
    replyInfo,
    getSenderDetails,
  ]);

  return <CommonMessageBubble {...chatMessageBubbleProps} />;
};
